import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PickSelect } from "@/components/apply/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { Panel, toIsoFromLocal } from "@/components/admin/Panel";
import { api, fmtDate, formatErr } from "@/lib/api";

const EMPTY = { farm_id: "", meetup_location: "", scheduled_at: "", details: "" };

export const IntroPanel = ({ app, intros, limit, onChange }) => {
  const [farms, setFarms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });
  useEffect(() => {
    api.get("/admin/farms").then((r) => setFarms(r.data)).catch(() => {});
  }, []);

  const assign = async () => {
    if (!form.farm_id || !form.meetup_location || !form.scheduled_at) return toast.error("Farm, location and time are required.");
    try {
      await api.post("/admin/introductions", { ...form, application_id: app.id, scheduled_at: toIsoFromLocal(form.scheduled_at) });
      setForm(EMPTY);
      toast.success("Introduction assigned - member emailed (mocked)");
      onChange();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const full = intros.length >= limit;
  return (
    <Panel title="Farm introductions" testId="panel-intros" action={<span className="text-sm font-bold" data-testid="intro-usage">{intros.length}/{limit} used</span>}>
      <div className="space-y-3 mb-5">
        {intros.length === 0 && <p className="text-sm text-muted-foreground">No introductions yet.</p>}
        {intros.map((i) => (
          <div key={i.id} className="rounded-xl border border-border p-3 text-sm flex flex-wrap items-start justify-between gap-3" data-testid={`intro-item-${i.id}`}>
            <div>
              <p className="font-bold">{i.farm_name} <span className="text-muted-foreground font-medium">· {i.farm_region}</span></p>
              <p className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{i.meetup_location} · {fmtDate(i.scheduled_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={i.status} />
              {i.status === "Scheduled" && <button onClick={() => act(() => api.patch(`/admin/introductions/${i.id}`, { status: "Completed" }), "Marked completed")} className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary" data-testid={`intro-complete-${i.id}`} aria-label="Complete"><CheckCircle2 className="h-4 w-4 text-primary" /></button>}
              <button onClick={() => act(() => api.delete(`/admin/introductions/${i.id}`), "Removed")} className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary" data-testid={`intro-delete-${i.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-destructive" /></button>
            </div>
          </div>
        ))}
      </div>
      {full ? <p className="text-sm text-amber-600 dark:text-amber-400 font-semibold" data-testid="intro-limit-reached">Tier limit reached. Raise the intro limit in Vetting to assign more.</p> : (
        <div className="grid gap-3 sm:grid-cols-2">
          <PickSelect value={form.farm_id} onChange={set("farm_id")} options={farms.map((f) => ({ value: f.id, label: `${f.name}${f.region ? ` · ${f.region}` : ""}` }))} placeholder={farms.length ? "Choose farm" : "Add a farm first"} testId="intro-farm-select" />
          <Input type="datetime-local" value={form.scheduled_at} onChange={set("scheduled_at")} className="h-12 rounded-xl" data-testid="intro-datetime-input" />
          <Input placeholder="Meetup location" value={form.meetup_location} onChange={set("meetup_location")} className="h-11 rounded-xl sm:col-span-2" data-testid="intro-location-input" />
          <Textarea placeholder="Intro details for the member" rows={2} value={form.details} onChange={set("details")} className="rounded-xl sm:col-span-2" data-testid="intro-details-input" />
          <button onClick={assign} className="sm:col-span-2 h-11 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2" data-testid="intro-assign-button"><Plus className="h-4 w-4" />Assign introduction</button>
        </div>
      )}
    </Panel>
  );
};
